import React, { useEffect, useRef, useState } from 'react';
import SEO from '@/components/layout/SEO';
import { MaterialIcon } from '@/components/ui/MaterialIcon';
import { submitContactMessage } from '@/lib/contact/web3forms';

type Status = 'idle' | 'sending' | 'success' | 'error';

type DiscoveryLink = {
    label: string;
    subtitle: string;
    icon: string;
    accent: 'secondary' | 'primary' | 'tertiary';
};

const DISCOVERY_LINKS: DiscoveryLink[] = [
    {
        label: 'ST_DISCORD',
        subtitle: 'COMMUNITY_CHANNEL',
        icon: 'groups',
        accent: 'secondary',
    },
    {
        label: 'ST_GITHUB',
        subtitle: 'SOURCE_PROTOCOLS',
        icon: 'code',
        accent: 'primary',
    },
    {
        label: 'ST_X_COMMS',
        subtitle: 'OS_UPDATES',
        icon: 'terminal',
        accent: 'tertiary',
    },
];

const METRICS = [
    { label: 'CPU LOAD', value: 33, color: 'secondary' },
    { label: 'MEM USE', value: 66, color: 'primary' },
    { label: 'UPLINK LVL', value: 25, color: 'tertiary' },
] as const;

export default function ContactPage() {
    const accessKey = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;
    const altchaChallengeUrl = import.meta.env.VITE_ALTCHA_CHALLENGE_URL;

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [altchaPayload, setAltchaPayload] = useState('');
    const [status, setStatus] = useState<Status>('idle');
    const [error, setError] = useState<string | null>(null);
    const altchaRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const scriptId = 'altcha-widget-script';
        if (document.getElementById(scriptId)) return;

        const script = document.createElement('script');
        script.id = scriptId;
        script.type = 'module';
        script.src = 'https://cdn.jsdelivr.net/npm/altcha/dist/altcha.min.js';
        document.head.appendChild(script);
    }, []);

    useEffect(() => {
        const widget = altchaRef.current;
        if (!widget) return;

        const onStateChange = (event: Event) => {
            const detail = (event as CustomEvent<{ state?: string; payload?: string }>).detail;
            if (detail?.state === 'verified' && detail.payload) {
                setAltchaPayload(detail.payload);
                return;
            }
            setAltchaPayload('');
        };

        widget.addEventListener('statechange', onStateChange as EventListener);
        return () => widget.removeEventListener('statechange', onStateChange as EventListener);
    }, []);

    const formReady = name.trim() !== '' && email.trim() !== '' && message.trim() !== '' && altchaPayload !== '';
    const bufferCount = Math.min(message.length, 4096);

    const submitLabel = status === 'sending' ? 'TRANSMITTING...' : 'Execute Transmission';
    const statusLabel = (() => {
        if (status === 'sending') return 'EXECUTION IN_PROGRESS';
        if (status === 'success') return 'TRANSMISSION_COMPLETE';
        if (status === 'error') return 'CRITICAL_FAULT: TRANSMISSION_FAILED';
        if (!formReady) return 'CRITICAL_FAULT: INPUT_REQUIRED';
        return 'SIGNAL_STABLE: READY_FOR_TRANSMISSION';
    })();

    const statusClass =
        status === 'success'
            ? 'text-[#00cffc] bg-[#00cffc]/10 border-[#00cffc]/30'
            : status === 'error' || !formReady
                ? 'text-[#ff716c] bg-[#9f0519]/10 border-[#ff716c]'
                : 'text-[#8cacff] bg-[#8cacff]/10 border-[#8cacff]/30';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!accessKey) {
            setError('Contact form is not configured. Set VITE_WEB3FORMS_ACCESS_KEY.');
            setStatus('error');
            return;
        }

        if (!altchaChallengeUrl) {
            setError('Bot protection is not configured. Set VITE_ALTCHA_CHALLENGE_URL.');
            setStatus('error');
            return;
        }

        if (!altchaPayload) {
            setError('Complete the verification challenge before sending.');
            setStatus('error');
            return;
        }

        setStatus('sending');
        try {
            await submitContactMessage({
                name,
                email,
                message,
                accessKey,
                altchaPayload,
            });

            setStatus('success');
            setName('');
            setEmail('');
            setMessage('');
            setAltchaPayload('');
            (altchaRef.current as { reset?: () => void } | null)?.reset?.();
        } catch (err: any) {
            setStatus('error');
            setError(err?.message || 'Unable to send message right now. Please try again soon.');
        }
    };

    return (
        <div className="min-h-screen bg-[#0e0e0e] text-[#e8eaf0]">
            <SEO
                title="STARTZONE.DEV | CONTACT_PROTOCOL"
                description="Open a secure uplink to the startzone.dev crew."
                canonicalUrl="https://startzone.dev/contact"
            />

            <main className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-12 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-6 lg:py-20">
                <section className="space-y-10 lg:col-span-8">
                    <header className="space-y-2 border-l-4 border-[#8cacff] pl-6">
                        <div className="flex items-center gap-2 font-['JetBrains_Mono'] text-xs uppercase tracking-[0.3em] text-[#00cffc]">
                            <MaterialIcon icon="leak_add" size={16} className="text-[#00cffc]" />
                            Uplink Status: Ready
                        </div>
                        <h1 className="font-['Space_Grotesk'] text-5xl font-bold uppercase tracking-[-0.06em] text-[#e8eaf0] drop-shadow-[0_0_18px_rgba(140,172,255,0.2)] md:text-6xl">
                            Initiate_Contact
                        </h1>
                        <p className="max-w-xl font-['Inter'] text-sm leading-7 text-[#acabaa] md:text-base">
                            Establish a secure line to the core architects. Secure encryption protocols active. Transmission latency: &lt;14ms.
                        </p>
                    </header>

                    <div className="relative overflow-hidden bg-[#131313] p-6 sm:p-8">
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0 opacity-20"
                            style={{
                                backgroundImage:
                                    'repeating-linear-gradient(to bottom, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 4px)',
                            }}
                        />

                        <form onSubmit={handleSubmit} className="relative z-10 space-y-8">
                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                                <label className="group space-y-2">
                                    <span className="flex items-center gap-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#8cacff]/60">
                                        [01] Operator_ID
                                    </span>
                                    <div className="relative">
                                        <span className="absolute left-0 top-1/2 ml-4 -translate-y-1/2 font-['JetBrains_Mono'] text-xs text-[#8cacff]">&gt;</span>
                                        <input
                                            type="text"
                                            required
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="UUID / USERNAME"
                                            className="w-full border-b border-[#484848]/30 bg-[#1f2020] py-4 pl-10 font-['JetBrains_Mono'] text-sm uppercase text-[#e8eaf0] outline-none transition-all placeholder:text-[#484848]/40 focus:border-[#8cacff] focus:ring-0"
                                        />
                                    </div>
                                </label>

                                <label className="space-y-2">
                                    <span className="flex items-center gap-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#8cacff]/60">
                                        [02] Comms_Frequency
                                    </span>
                                    <div className="relative">
                                        <span className="absolute left-0 top-1/2 ml-4 -translate-y-1/2 font-['JetBrains_Mono'] text-xs text-[#8cacff]">@</span>
                                        <input
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="ENCRYPTED_EMAIL"
                                            className="w-full border-b border-[#484848]/30 bg-[#1f2020] py-4 pl-10 font-['JetBrains_Mono'] text-sm uppercase text-[#e8eaf0] outline-none transition-all placeholder:text-[#484848]/40 focus:border-[#8cacff] focus:ring-0"
                                        />
                                    </div>
                                </label>
                            </div>

                            <div className="space-y-2">
                                <label className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#8cacff]/60">
                                    [03] Transmission_Payload
                                </label>
                                <div className="border-l-2 border-[#484848]/20 bg-[#1f2020] p-4 transition-colors focus-within:border-[#8cacff]">
                                    <textarea
                                        required
                                        rows={6}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="TYPE YOUR LOG ENTRY HERE..."
                                        className="w-full resize-none border-none bg-transparent font-['JetBrains_Mono'] text-sm uppercase text-[#e8eaf0] outline-none placeholder:text-[#484848]/40 focus:ring-0"
                                    />
                                    <div className="mt-2 flex justify-between border-t border-[#484848]/10 pt-2">
                                        <span className="font-['JetBrains_Mono'] text-[9px] uppercase text-[#767575]">
                                            UTF-8 Encoding Active
                                        </span>
                                        <span className="font-['JetBrains_Mono'] text-[9px] uppercase text-[#767575]">
                                            Buffer: {bufferCount}/4096
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-5 pt-2">
                                <div className="space-y-3">
                                    <p className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#8cacff]/60">
                                        [04] Bot_Check
                                    </p>
                                    <div className="border-l-2 border-[#484848]/20 bg-[#131313] p-4">
                                        <altcha-widget ref={altchaRef} challengeurl={altchaChallengeUrl} />
                                        <p className="mt-2 font-['JetBrains_Mono'] text-[9px] uppercase tracking-[0.2em] text-[#767575]">
                                            Verification gate active before transmission.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                                    <div className={`flex items-center gap-3 border-l-2 px-4 py-2 ${statusClass}`}>
                                        <MaterialIcon icon="report" size={14} className="text-current" />
                                        <span className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.3em]">
                                            {statusLabel}
                                        </span>
                                    </div>

                                    {error && status === 'error' && (
                                        <p className="max-w-md font-['Inter'] text-sm leading-6 text-[#ff716c]">
                                            {error}
                                        </p>
                                    )}

                                    {status === 'success' && (
                                        <p className="max-w-md font-['Inter'] text-sm leading-6 text-[#00cffc]">
                                            Message transmitted. The crew will reply through the uplink soon.
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={status === 'sending'}
                                        className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden bg-gradient-to-r from-[#8cacff] to-[#769dff] px-10 py-4 font-['Space_Grotesk'] text-sm font-bold uppercase tracking-[0.25em] text-[#0e0e0e] transition-all hover:brightness-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
                                    >
                                        <span className="relative z-10 flex items-center gap-2">
                                            {submitLabel}
                                            <MaterialIcon icon="send" size={18} className="text-current" />
                                        </span>
                                        <span
                                            aria-hidden="true"
                                            className="absolute inset-0 translate-y-full bg-white/10 transition-transform duration-300 group-hover:translate-y-0"
                                        />
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </section>

                <aside className="space-y-6 lg:col-span-4">
                    <div className="bg-[#1f2020] p-6">
                        <div className="mb-6 flex items-center justify-between">
                            <span className="font-['JetBrains_Mono'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#00cffc]">
                                Node_Discovery
                            </span>
                            <MaterialIcon icon="hub" size={18} className="text-[#00cffc]" />
                        </div>

                        <div className="space-y-6">
                            {DISCOVERY_LINKS.map((item) => (
                                <a
                                    key={item.label}
                                    href="#"
                                    onClick={(event) => event.preventDefault()}
                                    className={[
                                        'group flex items-center gap-4 border-l-2 border-transparent bg-[#25262680] p-3 transition-colors',
                                        item.accent === 'secondary' ? 'hover:border-[#00cffc] hover:bg-[#00cffc]/10' : '',
                                        item.accent === 'primary' ? 'hover:border-[#8cacff] hover:bg-[#8cacff]/10' : '',
                                        item.accent === 'tertiary' ? 'hover:border-[#d674ff] hover:bg-[#d674ff]/10' : '',
                                    ].join(' ')}
                                >
                                    <div
                                        className={[
                                            'flex h-10 w-10 items-center justify-center bg-[#131313]',
                                            item.accent === 'secondary' ? 'text-[#00cffc]' : '',
                                            item.accent === 'primary' ? 'text-[#8cacff]' : '',
                                            item.accent === 'tertiary' ? 'text-[#d674ff]' : '',
                                        ].join(' ')}
                                    >
                                        <MaterialIcon icon={item.icon} size={20} className="text-current" />
                                    </div>
                                    <div>
                                        <div className="font-['Space_Grotesk'] text-xs font-bold uppercase tracking-[0.25em] text-[#e8eaf0]">
                                            {item.label}
                                        </div>
                                        <div className="font-['JetBrains_Mono'] text-[9px] uppercase text-[#acabaa]">
                                            {item.subtitle}
                                        </div>
                                    </div>
                                    <MaterialIcon
                                        icon="arrow_outward"
                                        size={14}
                                        className="ml-auto opacity-0 transition-opacity group-hover:opacity-100"
                                    />
                                </a>
                            ))}
                        </div>
                    </div>

                    <div className="bg-[#131313] p-6">
                        <div className="mb-4 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#767575]">
                            Internal_Metrics
                        </div>

                        <div className="space-y-4">
                            {METRICS.map((metric) => (
                                <div key={metric.label} className="flex items-center justify-between gap-4">
                                    <span className="font-['JetBrains_Mono'] text-[9px] uppercase text-[#acabaa]">
                                        {metric.label}
                                    </span>
                                    <div className="relative h-1 w-32 bg-[#252626]">
                                        <div
                                            className={[
                                                'absolute left-0 top-0 h-full shadow-[0_0_8px_rgba(255,255,255,0.15)]',
                                                metric.color === 'secondary' ? 'bg-[#00cffc]' : '',
                                                metric.color === 'primary' ? 'bg-[#8cacff]' : '',
                                                metric.color === 'tertiary' ? 'bg-[#d674ff]' : '',
                                            ].join(' ')}
                                            style={{ width: `${metric.value}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 border-t border-[#484848]/10 pt-4 text-center">
                            <div className="animate-pulse font-['JetBrains_Mono'] text-[8px] uppercase text-[#767575]">
                                Scanning for incoming packets...
                            </div>
                        </div>
                    </div>

                    <div className="group relative h-32 overflow-hidden bg-[#131313]">
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 opacity-90"
                            style={{
                                backgroundImage: [
                                    'radial-gradient(circle at 20% 20%, rgba(0,207,252,0.35), transparent 20%)',
                                    'radial-gradient(circle at 80% 70%, rgba(140,172,255,0.28), transparent 18%)',
                                    'linear-gradient(to top, rgba(14,14,14,1), rgba(14,14,14,0.15))',
                                    'repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 8px)',
                                ].join(','),
                                filter: 'saturate(0.9)',
                            }}
                        />
                        <div className="absolute bottom-4 left-4 font-['JetBrains_Mono'] text-[10px] uppercase text-[#8cacff]">
                            [CORE_MAP_v0.9]
                        </div>
                    </div>
                </aside>
            </main>
        </div>
    );
}
